import type { TFunction } from "i18next";
import { VelavuPermission, VelavuRole } from "velavu-js-api";
import { mapUserRole } from "./language-helper";

// ---------------------------------------------------------------------------
// Roles are hierarchical: each role inherits the permissions of the role
// below it. Admin is granted every permission. These sets mirror the
// backend's role/permission mapping, listing only the permissions newly
// granted at each tier.
// ---------------------------------------------------------------------------

/** Roles ordered from least to most privileged. */
export const ROLE_HIERARCHY: readonly VelavuRole[] = [
	VelavuRole.Viewer,
	VelavuRole.Operator,
	VelavuRole.SiteManager,
	VelavuRole.Admin,
];

/** Returns roles at or below `maxRole` (inclusive), least-privileged first. */
export function getAssignableRoles(maxRole: VelavuRole): VelavuRole[] {
	const maxIndex = ROLE_HIERARCHY.indexOf(maxRole);
	if (maxIndex === -1) {
		return [];
	}
	return ROLE_HIERARCHY.slice(0, maxIndex + 1);
}

/** True when `role` is strictly higher in the hierarchy than `reference`. */
export function isRoleHigherThan(
	role: VelavuRole,
	reference: VelavuRole,
): boolean {
	return ROLE_HIERARCHY.indexOf(role) > ROLE_HIERARCHY.indexOf(reference);
}

const VIEWER_PERMISSIONS = new Set<VelavuPermission>([
	"organization:read",
	"users:read",
	"users:write-self",
	"assets:read",
	"devices:read",
	"events:read",
	"alerts:read",
	"sites:read",
	"geofences:read",
	"inventories:read",
	"backends:read",
	"mqtt:read",
	"mqtt:authorize",
	"integrations:read",
]);

const OPERATOR_PERMISSIONS = new Set<VelavuPermission>([
	"assets:write",
	"assets:delete",
	"alerts:delete",
	"inventories:manage",
	"devices:control",
]);

const SITE_MANAGER_PERMISSIONS = new Set<VelavuPermission>([
	"sites:write",
	"sites:delete",
	"sites:manage",
	"geofences:write",
	"geofences:delete",
	"inventories:write",
	"inventories:delete",
	"rules:read",
	"rules:write",
	"rules:delete",
	"integrations:write",
	"devices:write",
	"devices:manage",
	"dfu:read",
	"dfu:write",
	"users:write",
	"users:delete",
	"organization:write",
]);

/**
 * Returns the lowest role that is granted a given permission. Permissions not
 * explicitly granted to Viewer, Operator, or Site manager are Admin-only.
 */
export function getMinimumRoleForPermission(
	permission: VelavuPermission,
): VelavuRole {
	if (VIEWER_PERMISSIONS.has(permission)) {
		return VelavuRole.Viewer;
	} else if (OPERATOR_PERMISSIONS.has(permission)) {
		return VelavuRole.Operator;
	} else if (SITE_MANAGER_PERMISSIONS.has(permission)) {
		return VelavuRole.SiteManager;
	} else {
		return VelavuRole.Admin;
	}
}

/**
 * Builds a "Requires (role) or higher" tooltip string for a denied permission
 */
export function getPermissionDeniedMessage(
	permission: VelavuPermission,
	t: TFunction,
): string {
	const role = getMinimumRoleForPermission(permission);
	return t("common.permission_denied_role", { role: mapUserRole(role, t) });
}
